/**
 * NumberInput — numeric input with natural editing semantics (string draft,
 * tolerant parse, clamp-on-blur with flash).
 */
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { cx } from "@shared/cx";
import a11y from "@shared/a11y.module.css";
import styles from "./NumberInput.module.css";

interface NumberInputProps {
  /**
   * null renders an empty input and stays empty on blur — used for values
   * that are genuinely unset (an unpriced scope row's rate, #100) where
   * settling to 0 would fabricate a real value. onChange still only fires
   * with numbers: the field leaves null the moment the user types one.
   */
  value: number | null;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number | string;
  fallback?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  "aria-label"?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    { value, onChange, min, max, step, fallback, className, ...rest },
    externalRef,
  ) {
    const internalRef = useRef<HTMLInputElement>(null);
    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof externalRef === "function") externalRef(node);
        else if (externalRef) externalRef.current = node;
      },
      [externalRef],
    );

    const [draft, setDraft] = useState(String(value ?? ""));
    const [flash, setFlash] = useState(false);
    const [announcement, setAnnouncement] = useState("");

    useEffect(() => {
      if (
        typeof document !== "undefined" &&
        document.activeElement === internalRef.current
      ) {
        return;
      }
      setDraft(String(value ?? ""));
    }, [value]);

    const flashCorrection = (settled: number) => {
      setFlash(true);
      setAnnouncement(`Adjusted to ${settled}`);
      window.setTimeout(() => setFlash(false), 800);
      window.setTimeout(() => setAnnouncement(""), 1500);
    };

    return (
      <>
        <input
          ref={setRefs}
          type="text"
          inputMode="decimal"
          step={step}
          value={draft}
          className={cx(className, flash && styles.flash)}
          onChange={(e) => setDraft(e.target.value)}
          onWheel={(e) => e.currentTarget.blur()}
          onBlur={() => {
            const trimmed = draft.trim();
            const normalised = trimmed.replace(/\s/g, "").replace(/,/g, ".");
            const parsed = Number(normalised);
            const settle = (n: number, corrected: boolean) => {
              setDraft(String(n));
              if (n !== value) onChange(n);
              if (corrected) flashCorrection(n);
            };
            if (trimmed === "" || !Number.isFinite(parsed)) {
              // A null value stays null on an empty blur — settling it to a
              // number would silently price an unpriced row.
              if (value === null && trimmed === "") {
                return;
              }
              const f = fallback ?? min ?? 0;
              settle(f, trimmed !== "" && trimmed !== String(f));
              return;
            }
            let clamped = parsed;
            let corrected = false;
            if (min !== undefined && clamped < min) {
              clamped = min;
              corrected = true;
            }
            if (max !== undefined && clamped > max) {
              clamped = max;
              corrected = true;
            }
            settle(clamped, corrected);
          }}
          {...rest}
        />
        <span aria-live="polite" className={a11y.srOnly}>
          {announcement}
        </span>
      </>
    );
  },
);
